import React, { useState, useEffect } from 'react';
import { Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import styled from 'styled-components';
import HealtheraTable from '../../../../../components/Table/Table';
import useDebouncedOrganisationSearch from '../../../CreateClinic/hooks/useOrganisationSearch';
import useDebouncedPharmacySearch from '../../../CreateClinic/hooks/usePharmacySearch';
import { Organisation } from '../../../../../types/organisation';
import { Pharmacy } from '../../../../../types/pharmacy';
import { ModifyClinicProps } from '../../../types';
import TableContainer from '../../../../Shared/TableContainer';
import useFulfilments from '../../hooks/useFulfilments';
import useListingOrganisations from '../../hooks/useListingOrganisations';
import { Fulfilment, FulfilmentVisibility, FulfilmentType, FulfilmentTypeEnum } from '../../../../../apis/clinics/types';
import SearchOrganisations from './ClinicFulfilment/SearchOrganisations';
import SearchPharmacies from './ClinicFulfilment/SearchPharmacies';
import FulfilmentTypeSelect from './ClinicFulfilment/FulfilmentTypeSelect';

interface ClinicFulfilmentTabProps {
  clinic: ModifyClinicProps['clinic'];
}

const ClinicFulfilmentTab: React.FC<ClinicFulfilmentTabProps> = ({ clinic }) => {
  const { searchInput: orgSearchInput, setSearchInput: setOrgSearchInput, organisations } = useDebouncedOrganisationSearch();
  const { searchInput: pharmacySearchInput, setSearchInput: setPharmacySearchInput, pharmacies } = useDebouncedPharmacySearch();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { fulfilments, totalCount, addFulfilment, removeFulfilment, updateFulfilmentType } = useFulfilments(clinic._id, page, rowsPerPage);
  const { organisations: listingOrganisations } = useListingOrganisations(clinic._id, page, rowsPerPage);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Fulfilment | null>(null);
  const [fulfilmentType, setFulfilmentType] = useState<FulfilmentTypeEnum>(
    clinic.fulfilment_type === undefined || clinic.fulfilment_type === null ? FulfilmentTypeEnum.NONE : clinic.fulfilment_type
  );
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  useEffect(() => {
    setFulfilmentType(clinic.fulfilment_type === undefined || clinic.fulfilment_type === null
      ? FulfilmentTypeEnum.NONE
      : clinic.fulfilment_type);
  }, [clinic]);

  const isGlobalDefault = Boolean(clinic.selection_visibility === FulfilmentVisibility.GLOBAL_DEFAULT);
  const isTypeAllOrganisationsFulfilment = fulfilmentType === FulfilmentTypeEnum.MARKETPLACE;
  const isWithinListingNetwork = fulfilmentType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK;
  const searchFieldsDisabled = isGlobalDefault || isTypeAllOrganisationsFulfilment || isWithinListingNetwork;

  const handleAddOrganisation = async (organisation: Organisation) => {
    await addFulfilment({ organisation_id: organisation._id });
    setOrgSearchInput('');
  };

  const handleAddPharmacy = async (pharmacy: Pharmacy) => {
    await addFulfilment({ pharmacy_id: pharmacy._id });
    setPharmacySearchInput('');
  };

  const handleRemoveFulfilment = async (fulfilmentId: string) => {
    await removeFulfilment(fulfilmentId);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteClick = (item: Fulfilment) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleConfirmTypeChange = async () => {
    await updateFulfilmentType(fulfilmentType);
    setConfirmationDialogOpen(false);
  };

  const handleCancelTypeChange = () => {
    setFulfilmentType(clinic.fulfilment_type === undefined || clinic.fulfilment_type === null
      ? FulfilmentTypeEnum.NONE
      : clinic.fulfilment_type);
    setConfirmationDialogOpen(false);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const prepareFulfilmentData = (items: Fulfilment[]) => {
    return [
      {
        view: {
          name: 'Name',
          type: 'Type',
        },
        headers: true
      },
      ...items.map(item => ({
        view: {
          name: item.organisation?.name || item.pharmacy?.pharmacy_name,
          type: item.organisation ? FulfilmentType.ORGANISATION : FulfilmentType.PHARMACY,
        },
        button: !isGlobalDefault ? {
          title: 'Remove',
          onClick: () => handleDeleteClick(item)
        } : undefined
      }))
    ];
  };

  const messages = {
    globalDefault: 'This is a Healthera Clinic with global default settings. No fulfilments can be added.',
    networkFulfilment: 'This clinic uses Healthera Network Fulfilment. No fulfilments can be added.',
    searchDisabled: 'Search disabled for Healthera Clinic or Healthera Network Fulfilment',
    addFulfilments: 'No fulfilments have been added yet. Search for an organisation or pharmacy above to add one',
    listedOrg: 'This clinic will use the selected list of pharmacies and organisations',
    networkSettings: 'This clinic will use the Healthera Network fulfillment settings',
    withinListingNetwork: 'This clinic will use the listing organisation network for fulfillment'
  };

  const getFulfilmentTypeLabel = (type: FulfilmentTypeEnum) => {
    switch (type) {
      case FulfilmentTypeEnum.MARKETPLACE:
        return 'Healthera Marketplace Fulfilment';
      case FulfilmentTypeEnum.SPECIFIC_ORGANISATIONS:
        return 'Specific Organisations';
      case FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK:
        return 'Within Listing Organisation Network';
      case FulfilmentTypeEnum.NONE:
        return 'None';
      default:
        return '';
    }
  };

  const hasListedOrganisations = listingOrganisations.length > 0;

  return (
    <>
      <Container>
        <h2>&nbsp;</h2>
        {!isGlobalDefault && (
          <FulfilmentTypeSelect
            fulfilmentType={fulfilmentType}
            onTypeUpdate={async (type) => {
              await updateFulfilmentType(type);
              setFulfilmentType(type);
            }}
            hasListedOrganisations={listingOrganisations.length > 0}
            listedOrgMessage={messages.listedOrg}
            networkSettingsMessage={messages.networkSettings}
            withinListingNetworkMessage={messages.withinListingNetwork}
            isGlobalDefault={isGlobalDefault}
          />
        )}

        <ContentContainer>
          <SearchOrganisations
            searchInput={orgSearchInput}
            setSearchInput={setOrgSearchInput}
            organisations={organisations}
            handleAdd={handleAddOrganisation}
            disabled={searchFieldsDisabled}
            disabledMessage={messages.searchDisabled}
          />

          <SearchPharmacies
            searchInput={pharmacySearchInput}
            setSearchInput={setPharmacySearchInput}
            pharmacies={pharmacies}
            handleAdd={handleAddPharmacy}
            disabled={searchFieldsDisabled}
            disabledMessage={messages.searchDisabled}
          />
        </ContentContainer>

        <FulfilmentsTableContainer>
          {isWithinListingNetwork ? (
            listingOrganisations.length > 0 ? (
              <HealtheraTable
                data={[
                  {
                    view: {
                      name: 'Name',
                      type: 'Type',
                    },
                    headers: true
                  },
                  ...listingOrganisations.map(org => ({
                    view: {
                      name: org.name,
                      type: FulfilmentType.ORGANISATION,
                    }
                  }))
                ]}
                displayPagination={false}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                onChangePage={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                totalCount={listingOrganisations.length}
                isLiveDashboard={false}
                noHover={true}
                onRowClick={() => {}}
              />
            ) : (
              <EmptyStateContainer>
                <Typography variant="body1" color="textSecondary">
                  No listing organisations have been added yet. Please add listing organisations in the Listing Organisations tab.
                </Typography>
              </EmptyStateContainer>
            )
          ) : fulfilments.length > 0 ? (
            <HealtheraTable
              data={prepareFulfilmentData(fulfilments)}
              displayPagination={true}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              onChangePage={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              totalCount={totalCount}
              isLiveDashboard={false}
              noHover={true}
              onRowClick={() => {}}
            />
          ) : (
            <EmptyStateContainer>
              <Typography variant="body1" color="textSecondary">
                {isGlobalDefault
                  ? messages.globalDefault
                  : isTypeAllOrganisationsFulfilment
                    ? messages.networkFulfilment
                    : messages.addFulfilments}
              </Typography>
            </EmptyStateContainer>
          )}
        </FulfilmentsTableContainer>

        <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
          <DialogTitle>Remove Fulfilment</DialogTitle>
          <DialogContent>
            Are you sure you want to remove {itemToDelete?.organisation?.name || itemToDelete?.pharmacy?.pharmacy_name}?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => itemToDelete && handleRemoveFulfilment(itemToDelete._id)}
              color="secondary"
              variant="contained"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={confirmationDialogOpen} onClose={handleCancelTypeChange}>
          <DialogTitle>
            {fulfilmentType && fulfilmentType !== FulfilmentTypeEnum.NONE ? 'Change Fulfilment Type' : 'Remove Fulfilment Type'}
          </DialogTitle>
          <DialogContent>
            {fulfilmentType && fulfilmentType !== FulfilmentTypeEnum.NONE ? (
              <>
                Are you sure you want to change the fulfilment type to {getFulfilmentTypeLabel(fulfilmentType)}?
                {fulfilmentType === FulfilmentTypeEnum.MARKETPLACE && (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    This clinic's orders will be fulfilled by the Healthera network of pharmacies.
                  </Typography>
                )}
                {fulfilmentType === FulfilmentTypeEnum.WITHIN_LISTING_ORGANISATION_NETWORK && !hasListedOrganisations && (
                  <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                    Please add at least one listing organisation in the Listing Organisations tab before selecting this option.
                  </Typography>
                )}
              </>
            ) : (
              <>
                Are you sure you want to remove the fulfilment type? This will remove all fulfilment options for this clinic.
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  The clinic will not be able to fulfill any prescriptions until a new fulfilment type is selected.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelTypeChange} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTypeChange}
              color="primary"
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 24px;
`;

const ContentContainer = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const FulfilmentsTableContainer = styled(TableContainer)`
  margin-top: 24px;
`;

const EmptyStateContainer = styled.div`
  padding: 24px;
  text-align: center;
`;

export default ClinicFulfilmentTab;
